import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getProduct, updateProduct, categoriesAPI } from '../../services/api';
import './EditProductDialog.css';

const EditProductDialog = ({ isOpen, onClose, productId }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
  });

  // Fetch product details
  const { data: product, isLoading } = useQuery(
    ['product', productId],
    () => getProduct(productId),
    {
      enabled: isOpen && !!productId,
    }
  );

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery(
    'categories',
    async () => {
      const response = await categoriesAPI.list();
      return response.data.data;
    },
    {
      enabled: isOpen,
    }
  );

  const categories = categoriesData || [];

  // Load product data into form
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        categoryId: product.category_id || '',
        description: product.description || '',
      });
    }
  }, [product]);

  // Update mutation
  const updateMutation = useMutation(
    () => updateProduct(productId, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['product', productId]);
        queryClient.invalidateQueries('products');
        onClose();
      },
      onError: (error) => {
        alert(`Error updating product: ${error.message}`);
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Product name is required');
      return;
    }

    if (!formData.categoryId) {
      alert('Category is required');
      return;
    }

    updateMutation.mutate();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="edit-product-overlay">
      <div className="edit-product-dialog">
        <div className="edit-product-header">
          <h2>Edit Product</h2>
          <button className="close-button" onClick={handleCancel}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="edit-product-loading">Loading...</div>
        ) : (
          <form onSubmit={handleSave} className="edit-product-form">
            <div className="form-group">
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryId">Category *</label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter product description"
                rows="4"
              />
            </div>

            <div className="edit-product-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
                disabled={updateMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditProductDialog;
